export interface ReferralAttribution {
  heard_about_us: string;
  referred_by_name: string;
  referral_code: string;
}

const STORAGE_KEY = 'playce_referral_attribution_v1';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const EMPTY: ReferralAttribution = { heard_about_us: '', referred_by_name: '', referral_code: '' };
let memory: { value: ReferralAttribution; savedAt: number } | null = null;

export function normalizeAttribution(value: Partial<ReferralAttribution> = {}): ReferralAttribution {
  value = value ?? {};
  const code = typeof value.referral_code === 'string' ? value.referral_code.trim() : '';
  return {
    heard_about_us: typeof value.heard_about_us === 'string' ? value.heard_about_us.slice(0, 500) : '',
    referred_by_name: typeof value.referred_by_name === 'string' ? value.referred_by_name.slice(0, 200) : '',
    referral_code: /^[a-z0-9][a-z0-9_-]{0,127}$/i.test(code) ? code : '',
  };
}

export function loadAttribution(now = Date.now()): ReferralAttribution {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) memory = JSON.parse(raw);
  } catch { /* Retain same-page attribution if browser storage is unavailable. */ }
  if (!memory || !Number.isFinite(memory.savedAt) || now - memory.savedAt > MAX_AGE_MS) {
    memory = null;
    return { ...EMPTY };
  }
  return normalizeAttribution(memory.value);
}

export function saveAttribution(value: ReferralAttribution, now = Date.now()): void {
  memory = { value: normalizeAttribution(value), savedAt: now };
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(memory)); }
  catch { /* Optional attribution must never block a trial request. */ }
}

export function referralFromSearch(search: string, origin: string): string {
  const params = new URLSearchParams(search);
  let code = params.get('ref');
  if (!code && params.get('returnTo')) {
    try {
      const target = new URL(params.get('returnTo')!, origin);
      if (target.origin === origin && target.pathname === '/classes') code = target.searchParams.get('ref');
    } catch { /* Ignore malformed return URLs. */ }
  }
  return normalizeAttribution({ referral_code: code ?? '' }).referral_code;
}

export function withReferral(path: string, code: string): string {
  const valid = normalizeAttribution({ referral_code: code }).referral_code;
  if (!valid) return path;
  const url = new URL(path, 'https://playceacademy.com');
  url.searchParams.set('ref', valid);
  return `${url.pathname}${url.search}${url.hash}`;
}

export function hasAttribution(value: ReferralAttribution): boolean {
  return Boolean(value.heard_about_us.trim() || value.referred_by_name.trim() || value.referral_code);
}
